import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  AddParticipantForm,
  Modal,
  ParticipantCard,
  ParticipantsList,
  Skeleton,
  useTravelPurchaseContext
} from 'components';
import { useFetch, useSafeState, useUser } from 'hooks';
import { setTravelPurchase } from 'store';

const TravelParticipants = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [participantListModalOpen, setParticipantListModalOpen] = useSafeState(false);
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useSafeState(false);
  /**
   * State saved on click on a participant card
   * Keep track of the passenger category (adult, infant etc.) and the index of the participant in the list
   */
  const [participantCardData, setParticipantCardData] = useSafeState(null);

  const storedParticipants = useSelector((state) => state.travelPurchase.participants);
  const { setValue, watch } = useFormContext();
  const configurationParticipants = watch('configuration.participants');

  /* currentUser: minimal informations about user (serialNumber, companyId, firstName, name) stored in the cookies */
  const { currentUser } = useUser();
  /* user: full informations about user */
  const { user, userLoading } = useTravelPurchaseContext();

  const {
    call: rightHoldersCall,
    data: rightHolders,
    loading: rightHoldersLoading,
    setData: setRightHolders
  } = useFetch({ withSetState: false });

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getRightHolders();
  }, []);

  useEffect(() => {
    initFormParticipants();
  }, [user]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getRightHolders = async () => {
    const { data } =
      (await rightHoldersCall({
        service: 'castelis',
        path: `/users/rightHolders/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    if (!isEmpty(data?.body?.rightHolders)) {
      const updatedRightHolders = data.body.rightHolders.map((rightHolder) => ({
        ...rightHolder,
        /**
         * selected: used to display check/uncheck status of the checkbox inside ParticipantsList
         * Preselect participants that are already selected in the storedParticipants array
         */
        selected: storedParticipants.some((participant) => participant.serialNumber === rightHolder.serialNumber),
        /**
         * locked: used to know if participant has already been selected and assigned to a ParticipantCard
         * If locked is true, the participant can't be unselected in the list
         */
        locked: storedParticipants.some((participant) => participant.serialNumber === rightHolder.serialNumber)
      }));
      setRightHolders(updatedRightHolders);
    }
  };

  const initFormParticipants = () => {
    if (isEmpty(user)) return;

    // Init participants with values in the redux stored
    setValue('configuration.participants', storedParticipants);
  };

  const openModal = (participant, index) => {
    setParticipantCardData({ category: participant.passengerCategory, index });
    setParticipantListModalOpen(true);
  };

  const openAddParticipantModal = () => {
    setParticipantListModalOpen(false);
    setAddParticipantModalOpen(true);
  };

  const onAddNewParticipant = () => {
    setAddParticipantModalOpen(false);
    setParticipantListModalOpen(true);
    getRightHolders();
  };

  /* Add the informations of the selected participant to the card */
  const addParticipantToCard = (participantToAdd) => {
    const updatedParticipants = [...configurationParticipants];
    updatedParticipants[participantCardData.index] = {
      ...updatedParticipants[participantCardData.index],
      ...participantToAdd
    };
    setValue('configuration.participants', updatedParticipants, { shouldValidate: true });
    updateParticipantsList(participantToAdd.serialNumber, true);
    setParticipantListModalOpen(false);

    // Update participants data in the redux store
    dispatch(setTravelPurchase({ key: 'participants', value: updatedParticipants }));
  };

  /* Remove the informations of the participant from the card and the form */
  const removeParticipantFromCard = (participant, index) => {
    const updatedParticipants = [...configurationParticipants];
    updatedParticipants[index] = {
      ...updatedParticipants[index],
      name: '',
      firstName: '',
      birthDate: null,
      serialNumber: null
    };

    updateParticipantsList(participant.serialNumber, false);

    /* Update participants data in the form */
    setValue('configuration.participants', updatedParticipants, { shouldValidate: true });

    // Update participants data in the redux store
    dispatch(setTravelPurchase({ key: 'participants', value: updatedParticipants }));
  };

  const updateParticipantsList = (participantSerialNumber, selected) => {
    const updatedRightHolders = rightHolders.map((rightHolder) => {
      if (rightHolder.serialNumber === participantSerialNumber) {
        return { ...rightHolder, selected, locked: selected };
      }
      return rightHolder;
    });

    /* Update rightHolders array to unselect the participant from the list */
    setRightHolders(updatedRightHolders);
  };

  return (
    <div className="bg-white w-full h-auto rounded-xl p-2 md:p-4">
      <h2 className="uppercase text-sm font-medium lg:h2 underlined-blue mb-4 lg:mb-8">
        {t('purchase:configuration.yourParticipants')}
      </h2>

      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:gap-5 md:grid-cols-3">
        {userLoading && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={`skeleton-card-${index}`} classNames="h-32 w-full col-span-1" />
            ))}
          </>
        )}

        {!userLoading &&
          user &&
          !isEmpty(configurationParticipants) &&
          configurationParticipants.map((participant, index) => (
            <ParticipantCard
              key={`participant-card-${index}`}
              participant={participant}
              openModal={() => openModal(participant, index)}
              onRemoveParticipant={() => removeParticipantFromCard(participant, index)}
            />
          ))}
      </div>

      <Modal
        isOpen={participantListModalOpen}
        size="medium"
        header={<h2 className="h2 underlined-yellow">{t('purchase:configuration.participants')}</h2>}
        body={
          <>
            {rightHoldersLoading && (
              <>
                <Skeleton classNames="h-12 w-full mb-2" />
                <Skeleton classNames="h-8 w-full mb-4" />
                <Skeleton classNames="h-8 w-full" />
              </>
            )}
            {!rightHoldersLoading && (
              <ParticipantsList
                passengerCategory={participantCardData?.category}
                participants={rightHolders || []}
                onAddParticipant={addParticipantToCard}
                openAddParticipantModal={openAddParticipantModal}
              />
            )}
          </>
        }
        onClose={() => setParticipantListModalOpen(false)}
      />

      <Modal
        isOpen={addParticipantModalOpen}
        size="medium"
        header={<h2 className="h2 underlined-yellow">{t('purchase:configuration.addParticipantTitle')}</h2>}
        body={
          <AddParticipantForm passengerCategory={participantCardData?.category} onAddSuccess={onAddNewParticipant} />
        }
        onClose={() => setAddParticipantModalOpen(false)}
      />
    </div>
  );
};

export default TravelParticipants;
